<template>
  <model-form
    ref="modelForm"
    :model="pagerRequestType"
    modal-type="modal"
  >
    <div class="column">
      <div class="row q-gutter-md items-start q-mt-sm">
        <span class="text-body1 text-bold">
          General
        </span>
      </div>
      <div class="row q-gutter-md items-start">
        <span class="text-caption">
          <ul class="q-pl-md">
            <li>The internal name is used on the Point of Sale to identify the pager request type.</li>
            <li>The display name is used on the consumer website to display the pager request type.</li>
          </ul>
        </span>
      </div>
      <div class="row q-gutter-md items-start">
        <q-input
          v-model="pagerRequestType.name"
          class="col"
          label="Internal Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          autofocus
        />
        <q-input
          v-model="pagerRequestType.displayName"
          class="col"
          label="Display Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
      </div>
      <div class="row q-gutter-md items-start">
        <span class="text-body1 text-bold">
          Icon
        </span>
      </div>
      <div class="row q-gutter-x-md q-mt-sm items-start">
        <q-btn
          v-if="$_.isNil(pagerRequestType.icon) && $_.isNil(pagerRequestType.iconImage)"
          label="Select Icon"
          class="col-4"
          outline
          @click="$refs.iconPickerDialog.show()"
        />
        <q-btn
          v-else
          size="lg"
          class="col-4"
          outline
          color="primary"
          @click="$refs.iconPickerDialog.show()"
        >
          <q-avatar v-if="pagerRequestType.iconImage">
            <img :src="pagerRequestType.iconImagePreviewUrl">
          </q-avatar>
          <q-icon
            v-else
            :name="pagerRequestType.icon"
          />
        </q-btn>
        <span class="text-caption col">
          <ul class="q-pl-md q-mt-none">
            <li>The icon is used on the consumer website to display the pager request type.</li>
          </ul>
        </span>
      </div>
      <div class="row items-start q-mt-sm">
        <div class="row full-width">
          <span class="text-body1 text-bold">
            Availability
          </span>
        </div>
        <div class="row q-gutter-md items-start">
          <span class="text-caption">
            <ul class="q-pl-md">
              <li>The pager request type will be available at the selected delivery locations.</li>
            </ul>
          </span>
        </div>
        <div class="row full-width">
          <q-select
            v-model="pagerRequestType.deliveryLocationIds"
            class="col-12"
            label="Delivery Locations"
            multiple
            :options="deliveryLocations.data"
            option-label="name"
            option-value="id"
            emit-value
            map-options
            use-chips
          >
            <template #append>
              <q-btn
                round
                dense
                flat
                :icon="isAllDeliveryLocationsSelected ? 'fas fa-xmark' : 'fas fa-plus'"
                @click.stop.prevent="toggleAllDeliveryLocations"
              >
                <q-tooltip>
                  {{ isAllDeliveryLocationsSelected ? 'Remove All' : 'Add All' }}
                </q-tooltip>
              </q-btn>
            </template>
          </q-select>
        </div>
      </div>

      <div class="row q-gutter-md items-start q-mt-sm">
        <span class="text-body1 text-bold">
          Preview
        </span>
      </div>
      <div class="row items-start q-mt-sm">
        <q-banner
          class="full-width"
          rounded
          inline-actions
          :style="checkBoxBannerStyle(checked)"
        >
          <template #avatar>
            <q-avatar
              v-if="!$_.isNil(pagerRequestType.iconImage) || !$_.isNil(pagerRequestType.icon)"
              size="lg"
              :style="checkBoxAvatarStyle(checked)"
            >
              <img
                v-if="!$_.isNil(pagerRequestType.iconImage)"
                :src="pagerRequestType.iconImagePreviewUrl"
                style="width:24px; height: 24px;"
              >
              <q-icon
                v-else
                :name="pagerRequestType.icon"
                :color="$getColor(currentSite, 'warning')"
              />
            </q-avatar>
          </template>
          <div class="row items-center">
            <div class="col">
              <div class="text-weight-bold">
                {{ pagerRequestType.displayName || pagerRequestType.name }}
              </div>
            </div>
          </div>
          <template #action>
            <q-checkbox
              v-model="checked"
              class="text-capitalize col"
            />
          </template>
        </q-banner>
      </div>
      <icon-picker-dialog
        ref="iconPickerDialog"
        :model="pagerRequestType"
        icon-property="icon"
        icon-image="iconImage"
        icon-image-preview-url="iconImagePreviewUrl"
        allow-custom-image
      />
    </div>
  </model-form>
</template>
<script>

import IconPickerDialog from 'src/modules/admin/components/IconPickerDialog'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

export default {
  name: 'PagerRequestTypeForm',
  configuration: ['deliveryLocations', 'currentSite'],
  components: {
    IconPickerDialog,
    ModelForm
  },
  models: {
    pagerRequestType: {
      editing: true,
      deliveryLocations: 'default'
    }
  },
  data () {
    return {
      checked: false
    }
  },
  computed: {
    isAllDeliveryLocationsSelected () {
      return _.differenceBy(this.deliveryLocations.data, this.pagerRequestType.deliveryLocations.data, 'id').length === 0
    }
  },
  methods: {
    checkBoxBannerStyle (isSelected) {
      const colorHex = isSelected ? this.currentSite.positiveColor : this.currentSite.primaryColor
      if (isSelected) {
        return this.$q.dark.isActive
          ? `background-color: color-mix(in srgb, ${colorHex}, transparent 90%); border: 1px solid color-mix(in srgb, ${colorHex}, transparent 50%);`
          : `background-color: ${this.$lightenColor(colorHex, 90)}; border: 1px solid ${this.$lightenColor(colorHex, 50)};`
      } else {
        return this.$q.dark.isActive
          ? `border: 1px solid color-mix(in srgb, ${colorHex}, transparent 50%);`
          : `border: 1px solid ${this.$lightenColor(colorHex, 50)};`
      }
    },
    checkBoxAvatarStyle (isSelected) {
      const colorHex = isSelected ? '#ffffff' : this.currentSite.primaryColor
      return this.$q.dark.isActive
        ? `background-color: color-mix(in srgb, ${colorHex}, transparent 10%);`
        : `background-color: ${this.$lightenColor(colorHex, 90)}; `
    },
    toggleAllDeliveryLocations () {
      if (this.isAllDeliveryLocationsSelected) {
        this.pagerRequestType.deliveryLocationIds = []
      } else {
        this.pagerRequestType.deliveryLocationIds = _.map(this.deliveryLocations.data, 'id')
      }
    }
  }
}

</script>
